import React, { useState, useEffect } from 'react';
import { generateFlashCardHTML, generateMultipleChoiceQuizHTML, generateShortAnswerQuizHTML } from './utils/templateLoader';
import { loadWordsByLevel, getRandomWords } from './utils/wordDataLoader';
import { generatePassageWorksheetHTML } from './utils/passageTemplateLoader';
import WordSettingsModal from './components/WordSettingsModal';
import PassageSettingsModal from './components/PassageSettingsModal';
import ListeningGenerationModal from './components/ListeningGenerationModal';
import DeploymentModal from './components/DeploymentModal';
import Header from './components/Header';
import githubService from './utils/githubService';

const EnglishStudyComponent = () => {
  const [activeTab, setActiveTab] = useState('listening');
  const [showWordCardSettings, setShowWordCardSettings] = useState(false);
  const [showQuizSettings, setShowQuizSettings] = useState(false);
  const [showShortAnswerSettings, setShowShortAnswerSettings] = useState(false);
  const [showListeningSettings, setShowListeningSettings] = useState(false);
  const [wordCardSettings, setWordCardSettings] = useState({
    schoolLevel: '',
    grade: '',
    wordCount: '',
    sheetUrl: ''
  });
  const [quizSettings, setQuizSettings] = useState({
    schoolLevel: '',
    grade: '',
    wordCount: '',
    sheetUrl: ''
  });
  const [shortAnswerSettings, setShortAnswerSettings] = useState({
    schoolLevel: '',
    grade: '',
    wordCount: '',
    sheetUrl: ''
  });
  const [listeningSettings, setListeningSettings] = useState({
    schoolLevel: '',
    grade: '',
    topic: '',
    sheetUrl: ''
  });
  
  const [showPassageSettings, setShowPassageSettings] = useState(false);
  const [passageSettings, setPassageSettings] = useState({
    schoolLevel: '',
    grade: '',
    topic: '',
    passageSheetUrl: ''
  });
  const [isGeneratingPassage, setIsGeneratingPassage] = useState(false);
  const [passageGenerationProgress, setPassageGenerationProgress] = useState(null);
  const [passageAbortController, setPassageAbortController] = useState(null);

  // 단어 템플릿 진행률 관련 state
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [templateGenerationProgress, setTemplateGenerationProgress] = useState(null);
  const [currentTemplateType, setCurrentTemplateType] = useState('');

  // 듣기 문항 생성 관련 state
  const [isGeneratingListening, setIsGeneratingListening] = useState(false);

  // DeploymentModal 관련 state
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [deploymentData, setDeploymentData] = useState({
    htmlContent: '',
    fileName: '',
    contentType: ''
  });

  // 사용자 설정 로드 (Header에서 관리하므로 여기서 직접 로드)
  const [userSettings, setUserSettings] = useState({
    defaultSchoolLevel: '',
    defaultGrade: '',
    defaultSheetUrl: ''
  });

  // 사용자 설정 로드
  useEffect(() => {
    const loadUserSettings = () => {
      const savedSettings = localStorage.getItem('user_default_settings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          setUserSettings(parsedSettings);
          console.log('✅ 사용자 설정 로드됨:', parsedSettings);
        } catch (error) {
          console.error('❌ 사용자 설정 로드 실패:', error);
        }
      }
    };

    loadUserSettings();
  }, []);


  const menuItems = {
    listening: [
      { title: '수준별 듣기 문항 생성', description: '난이도에 맞는 듣기 문제를 생성합니다' },
      { title: '쉐도잉 기능', description: '원어민 음성을 따라 말하는 연습을 합니다' },
      { title: '스토리북 제작', description: '듣기 기반의 스토리북을 만들어 보세요' }
    ],
    writing: [
      { title: '에세이 자동채점', description: '학생들의 에세이를 AI로 채점해보세요' },
      { title: 'AI 맞춤 진로경로', description: 'AI가 분석한 진로기반으로 롤플레잉을 해보세요' }
    ],
    reading: [
      { title: '지문 생성', description: '주제에 맞는 지문을 생성합니다.' },
      { title: '문항 생성', description: '지문을 기반으로 문항을 생성합니다.' },
      { title: '문맥 이해하기', description: '문맥을 통해 의미를 파악하는 연습을 합니다' }
    ],
    vocabulary: [
      { title: '단어 카드 만들기', description: '나만의 단어장을 만들어 학습하세요' },
      { title: '객관식 퀴즈', description: '다양한 어휘 문제를 객관식으로 풀어보세요' },
      { title: '주관식 퀴즈', description: '직접 답을 작성하는 어휘 문제입니다' }
    ],

    aiEducation: [
      { title: 'AI 기반 피드백 추천', description: '개인 맞춤형 학습 피드백을 제안합니다' },
      { title: 'AI 기반 학습자료 확장', description: 'AI 튜터와의 대화를 학습자료를 확장합니다' },
      { title: 'AI 기반 교수설계', description: 'AI 튜터와의 대화를 통해 교수설계를 지원합니다' }
    ]
  };

  const tabNames = {
    listening: '듣기',
    writing: '쓰기',
    reading: '읽기',
    vocabulary: '어휘',
    aiEducation: 'AI로확장하기'
  };

  const handleWordCardClick = () => {
    // 저장된 기본값 적용
    setWordCardSettings({
      schoolLevel: userSettings.defaultSchoolLevel || '',
      grade: userSettings.defaultGrade || '',
      wordCount: '',
      sheetUrl: userSettings.defaultSheetUrl || ''
    });
    setShowWordCardSettings(true);
  };

  const handleQuizClick = () => {
    // 저장된 기본값 적용
    setQuizSettings({
      schoolLevel: userSettings.defaultSchoolLevel || '',
      grade: userSettings.defaultGrade || '',
      wordCount: '',
      sheetUrl: userSettings.defaultSheetUrl || ''
    });
    setShowQuizSettings(true);
  };

  const handleShortAnswerClick = () => {
    // 저장된 기본값 적용
    setShortAnswerSettings({
      schoolLevel: userSettings.defaultSchoolLevel || '',
      grade: userSettings.defaultGrade || '',
      wordCount: '',
      sheetUrl: userSettings.defaultSheetUrl || ''
    });
    setShowShortAnswerSettings(true);
  };

  const handleListeningClick = () => {
    // 저장된 기본값 적용
    setListeningSettings({
      schoolLevel: userSettings.defaultSchoolLevel || '',
      grade: userSettings.defaultGrade || '',
      topic: '',
      sheetUrl: userSettings.defaultSheetUrl || ''
    });
    setShowListeningSettings(true);
  };

  const handleShadowingClick = () => {
    // 새 탭에서 쉐도잉 생성기 열기
    window.open('/?mode=shadowing-generator', '_blank');
  };

  const handleEssayGradingClick = () => {
    // 새 탭에서 에세이 자동 채점 열기
    window.open('/?mode=essay-grading', '_blank');
  };

  const handleCareerPathClick = () => {
    // 새 탭에서 AI 맞춤 진로 경로 열기
    window.open('/?mode=career-path', '_blank');
  };

  const handlePassageGenerationClick = () => {
    // 저장된 기본값 적용
    setPassageSettings({
      schoolLevel: userSettings.defaultSchoolLevel || '',
      grade: userSettings.defaultGrade || '',
      topic: '',
      passageSheetUrl: userSettings.defaultSheetUrl || ''
    });
    setShowPassageSettings(true);
  };

  const handleQuestionGenerationClick = () => {
    // 새 창에서 문항 생성기 열기
    window.open('?mode=question-generator', '_blank');
  };

  const handleQuizSettingsConfirm = async () => {
    const { schoolLevel, grade, wordCount } = quizSettings;
    
    if (!schoolLevel || !grade || !wordCount) {
      alert('모든 항목을 선택해주세요.');
      return;
    }
    
    try {
      // 진행률 시작
      setIsGeneratingTemplate(true);
      setCurrentTemplateType('객관식 퀴즈');
      updateTemplateProgress(0, '단어 데이터를 로딩하고 있습니다...');
      
      const words = await generateWordData(schoolLevel, parseInt(grade), parseInt(wordCount));
      console.log('생성된 단어 데이터:', words);
      
      if (!words || words.length === 0) {
        alert('선택한 조건에 맞는 단어가 없습니다.');
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
        return;
      }
      
      updateTemplateProgress(1, '객관식 퀴즈 HTML을 생성하고 있습니다...');
      const htmlContent = await generateMultipleChoiceQuizHTML(words, quizSettings, quizSettings.sheetUrl);
      console.log('생성된 HTML 길이:', htmlContent.length);
      console.log('HTML 내용 미리보기:', htmlContent.substring(0, 200) + '...');
      
      if (!htmlContent || htmlContent.length < 100) {
        alert('HTML 생성에 실패했습니다.');
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
        return;
      }
      
      updateTemplateProgress(2, '배포를 위해 준비하고 있습니다...');
      
      // DeploymentModal 열기
      const schoolLevelEng = schoolLevel === '초등' ? 'elementary' : schoolLevel === '중등' ? 'middle' : 'high';
      const fileName = `quiz_${schoolLevelEng}_${grade}_${wordCount}words.html`;
      
      setDeploymentData({
        htmlContent: htmlContent,
        fileName: fileName,
        contentType: 'quizzes'
      });
      
      // 완료 후 잠시 대기 후 모달 열기
      setTimeout(() => {
        setShowDeploymentModal(true);
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
      }, 1000);
      
      // 설정 화면 닫기
      setShowQuizSettings(false);
      setQuizSettings({ schoolLevel: '', grade: '', wordCount: '', sheetUrl: '' });
    } catch (error) {
      console.error('객관식 퀴즈 생성 오류:', error);
      alert(`객관식 퀴즈 생성 중 오류가 발생했습니다: ${error.message}`);
      setIsGeneratingTemplate(false);
      setTemplateGenerationProgress(null);
    }
  };

  const handleShortAnswerSettingsConfirm = async () => {
    const { schoolLevel, grade, wordCount } = shortAnswerSettings;
    
    if (!schoolLevel || !grade || !wordCount) {
      alert('모든 항목을 선택해주세요.');
      return;
    }
    
    try {
      // 진행률 시작
      setIsGeneratingTemplate(true);
      setCurrentTemplateType('주관식 퀴즈');
      updateTemplateProgress(0, '단어 데이터를 로딩하고 있습니다...');
      
      const words = await generateWordData(schoolLevel, parseInt(grade), parseInt(wordCount));
      console.log('생성된 단어 데이터:', words);
      
      if (!words || words.length === 0) {
        alert('선택한 조건에 맞는 단어가 없습니다.');
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
        return;
      }
      
      updateTemplateProgress(1, '주관식 퀴즈 HTML을 생성하고 있습니다...');
      const htmlContent = await generateShortAnswerQuizHTML(words, shortAnswerSettings, shortAnswerSettings.sheetUrl);
      console.log('생성된 HTML 길이:', htmlContent.length);
      console.log('HTML 내용 미리보기:', htmlContent.substring(0, 200) + '...');
      
      if (!htmlContent || htmlContent.length < 100) {
        alert('HTML 생성에 실패했습니다.');
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
        return;
      }
      
      updateTemplateProgress(2, '배포를 위해 준비하고 있습니다...');
      
      // DeploymentModal 열기
      const schoolLevelEng = schoolLevel === '초등' ? 'elementary' : schoolLevel === '중등' ? 'middle' : 'high';
      const fileName = `short-answer-quiz_${schoolLevelEng}_${grade}_${wordCount}words.html`;
      
      setDeploymentData({
        htmlContent: htmlContent,
        fileName: fileName,
        contentType: 'quizzes'
      });
      
      // 완료 후 잠시 대기 후 모달 열기
      setTimeout(() => {
        setShowDeploymentModal(true);
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
      }, 1000);
      
      // 설정 화면 닫기
      setShowShortAnswerSettings(false);
      setShortAnswerSettings({ schoolLevel: '', grade: '', wordCount: '', sheetUrl: '' });
    } catch (error) {
      console.error('주관식 퀴즈 생성 오류:', error);
      alert(`주관식 퀴즈 생성 중 오류가 발생했습니다: ${error.message}`);
      setIsGeneratingTemplate(false);
      setTemplateGenerationProgress(null);
    }
  };

  const generateWordData = async (schoolLevel, grade, count) => {
    try {
      console.log(`단어 데이터 로딩: ${schoolLevel} ${grade}학년, ${count}개`);
      
      // JSON 파일에서 단어 데이터 로드
      const words = await loadWordsByLevel(schoolLevel, grade);
      console.log(`로드된 전체 단어 수: ${words.length}`);
      
      if (!words || words.length === 0) {
        console.warn(`${schoolLevel} ${grade}학년 단어 데이터가 없습니다.`);
        return [];
      }
      
      // 랜덤하게 요청한 개수만큼 선택
      const selectedWords = getRandomWords(words, count);
      console.log(`선택된 단어 수: ${selectedWords.length}`);
      
      // JSON 데이터가 이미 {english, korean} 형태로 되어 있음
      return selectedWords;
    } catch (error) {
      console.error('단어 데이터 생성 오류:', error);
      return [];
    }
  };

  const updateProgress = (stepIndex, currentStepText = null) => {
    const steps = [
      { label: '어휘 데이터 로딩 중...', status: 'pending' },
      { label: 'AI 지문 생성 중...', status: 'pending' },
      { label: '문법 분석 및 해설 생성 중...', status: 'pending' },
      { label: 'HTML 워크시트 생성 중...', status: 'pending' },
      { label: '파일 다운로드 준비 중...', status: 'pending' }
    ];

    steps.forEach((step, index) => {
      if (index < stepIndex) {
        step.status = 'completed';
      } else if (index === stepIndex) {
        step.status = 'current';
      }
    });

    setPassageGenerationProgress({
      steps,
      currentStep: currentStepText
    });
  };

  // 템플릿 생성 진행률 업데이트 함수
  const updateTemplateProgress = (stepIndex, currentStepText = null) => {
    const steps = [
      { label: '단어 데이터 로딩 중...', status: 'pending' },
      { label: 'HTML 템플릿 생성 중...', status: 'pending' },
      { label: '배포 준비 중...', status: 'pending' }
    ];

    steps.forEach((step, index) => {
      if (index < stepIndex) {
        step.status = 'completed';
      } else if (index === stepIndex) {
        step.status = 'active';
        if (currentStepText) {
          step.label = currentStepText;
        }
      }
    });

    setTemplateGenerationProgress({
      steps,
      currentStep: currentStepText
    });
  };

  // 듣기 문항 생성 함수
  const handleListeningGeneration = async () => {
    const { schoolLevel, grade, topic, sheetUrl } = listeningSettings;
    
    if (!schoolLevel || !grade) {
      alert('학교급과 학년을 선택해주세요.');
      return;
    }
    
    setIsGeneratingListening(true);
    
    try {
      console.log('듣기 문항 생성 요청:', { schoolLevel, grade, topic, sheetUrl });
      
      const response = await fetch('/api/generate-listening', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          schoolLevel,
          grade,
          topic: topic || undefined,
          sheetUrl
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log('듣기 문항 생성 성공:', result);
        
        // 생성된 데이터를 HTML로 변환해서 DeploymentModal에 전달
        // 일단 간단한 HTML로 테스트
        const htmlContent = generateListeningHTML(result);
        
        const schoolLevelEng = schoolLevel === 'elementary' ? 'elementary' : 
                              schoolLevel === 'middle' ? 'middle' : 
                              schoolLevel === 'high' ? 'high' : 'advanced';
        
        // 유니크 파일명 생성 (타임스탬프 + 랜덤 ID)
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19).replace(/[-:T]/g, ''); // YYYYMMDDHHMMSS
        const randomId = Math.random().toString(36).substr(2, 6); // 6자리 랜덤
        const fileName = `listening_${schoolLevelEng}_${grade}_${topic || 'auto'}_${timestamp}_${randomId}.html`;
        
        setDeploymentData({
          htmlContent: htmlContent,
          fileName: fileName,
          contentType: 'listening'
        });
        
        setShowDeploymentModal(true);
        setShowListeningSettings(false);
        
      } else {
        throw new Error(result.error || '듣기 문항 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('듣기 문항 생성 오류:', error);
      alert(`듣기 문항 생성 오류: ${error.message}`);
    } finally {
      setIsGeneratingListening(false);
    }
  };

  // 개선된 HTML 생성 함수
  const generateListeningHTML = (data) => {
    const questionsHTML = data.questions.map((q, index) => `
      <div class="question-card" id="question-${index}">
        <div class="question-header">
          <span class="question-number">문제 ${index + 1}</span>
          <span class="question-type">${q.type}</span>
        </div>
        <h3 class="question-title">${q.question}</h3>
        <div class="options-container">
          ${q.options.map((option, i) => `
            <label class="option-item" data-question="${index}" data-option="${i}">
              <input type="radio" name="q${index}" value="${i}" data-correct="${i === q.correctIndex}">
              <span class="option-number">${i + 1}</span>
              <span class="option-text">${option}</span>
              <span class="option-check">✓</span>
            </label>
          `).join('')}
        </div>
        <div class="explanation-box" style="display:none;">
          <div class="explanation-header">
            <strong>정답: ${q.correctIndex + 1}번</strong>
            <span class="accuracy-badge">${q.expectedAccuracy}</span>
          </div>
          <p class="explanation-text">${q.explanation}</p>
        </div>
      </div>
    `).join('');
    
    return `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>듣기 문항 - ${data.metadata.schoolLevel} ${data.metadata.grade}학년</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            max-width: 900px;
            margin: 0 auto;
            padding: 20px;
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            min-height: 100vh;
        }
        
        .header-section {
            background: white;
            border-radius: 15px;
            padding: 30px;
            margin-bottom: 30px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            text-align: center;
        }
        
        .main-title {
            font-size: 2.5em;
            color: #2c3e50;
            margin-bottom: 10px;
            font-weight: bold;
        }
        
        .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            margin: 20px 0;
        }
        
        .info-item {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 10px;
            border-left: 4px solid #3498db;
        }
        
        .audio-section {
            background: white;
            border-radius: 15px;
            padding: 30px;
            margin-bottom: 30px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            text-align: center;
        }
        
        .audio-title {
            font-size: 1.5em;
            color: #2c3e50;
            margin-bottom: 20px;
        }
        
        audio {
            width: 100%;
            max-width: 600px;
            margin: 20px 0;
            border-radius: 10px;
        }
        
        .speed-controls {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin: 20px 0;
            flex-wrap: wrap;
        }
        
        .speed-btn {
            background: #3498db;
            color: white;
            border: none;
            padding: 10px 15px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.3s ease;
        }
        
        .speed-btn:hover {
            background: #2980b9;
            transform: translateY(-2px);
        }
        
        .speed-btn.active {
            background: #e74c3c;
            box-shadow: 0 4px 8px rgba(231,76,60,0.3);
        }
        
        .play-counter {
            font-size: 1.1em;
            color: #e74c3c;
            margin: 15px 0;
            font-weight: bold;
        }
        
        .questions-section {
            display: grid;
            gap: 25px;
        }
        
        .question-card {
            background: white;
            border-radius: 15px;
            padding: 25px;
            box-shadow: 0 8px 20px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
            border: 3px solid transparent;
        }
        
        .question-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 30px rgba(0,0,0,0.15);
        }
        
        .question-card.answered {
            border-color: #3498db;
        }
        
        .question-card.correct {
            border-color: #27ae60;
            background: linear-gradient(135deg, #ffffff 0%, #f8fff8 100%);
        }
        
        .question-card.incorrect {
            border-color: #e74c3c;
            background: linear-gradient(135deg, #ffffff 0%, #fff8f8 100%);
        }
        
        .question-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
        }
        
        .question-number {
            background: #3498db;
            color: white;
            padding: 8px 15px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 1.1em;
        }
        
        .question-type {
            background: #95a5a6;
            color: white;
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 0.9em;
            text-transform: capitalize;
        }
        
        .question-title {
            font-size: 1.3em;
            color: #2c3e50;
            margin-bottom: 20px;
            line-height: 1.5;
        }
        
        .options-container {
            display: grid;
            gap: 12px;
            margin: 20px 0;
        }
        
        .option-item {
            display: flex;
            align-items: center;
            padding: 15px;
            border: 2px solid #ecf0f1;
            border-radius: 10px;
            cursor: pointer;
            transition: all 0.3s ease;
            background: #fdfdfd;
        }
        
        .option-item:hover {
            border-color: #3498db;
            background: #f8f9ff;
            transform: translateX(5px);
        }
        
        .option-item input[type="radio"] {
            display: none;
        }
        
        .option-number {
            background: #ecf0f1;
            color: #7f8c8d;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            margin-right: 15px;
            transition: all 0.3s ease;
        }
        
        .option-text {
            flex: 1;
            font-size: 1.1em;
            color: #2c3e50;
        }
        
        .option-check {
            opacity: 0;
            color: #27ae60;
            font-size: 1.5em;
            font-weight: bold;
            transition: all 0.3s ease;
        }
        
        .option-item input:checked ~ .option-number {
            background: #3498db;
            color: white;
        }
        
        .option-item input:checked ~ .option-check {
            opacity: 1;
        }
        
        .option-item.correct {
            border-color: #27ae60;
            background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
        }
        
        .option-item.correct .option-number {
            background: #27ae60;
            color: white;
        }
        
        .option-item.incorrect {
            border-color: #e74c3c;
            background: linear-gradient(135deg, #fff8f8 0%, #f8e8e8 100%);
        }
        
        .option-item.incorrect .option-number {
            background: #e74c3c;
            color: white;
        }
        
        .explanation-box {
            margin-top: 20px;
            padding: 20px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 10px;
            border-left: 5px solid #3498db;
        }
        
        .explanation-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
        }
        
        .accuracy-badge {
            background: #f39c12;
            color: white;
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 0.9em;
            font-weight: bold;
        }
        
        .explanation-text {
            font-size: 1.1em;
            color: #2c3e50;
            line-height: 1.6;
        }
        
        .action-buttons {
            text-align: center;
            margin: 30px 0;
            display: flex;
            gap: 15px;
            justify-content: center;
            flex-wrap: wrap;
        }
        
        .btn {
            padding: 15px 30px;
            border: none;
            border-radius: 25px;
            font-size: 1.1em;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .btn-primary {
            background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
            color: white;
        }
        
        .btn-primary:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(52,152,219,0.3);
        }
        
        .btn-success {
            background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
            color: white;
        }
        
        .btn-success:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(39,174,96,0.3);
        }
        
        .btn-info {
            background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
            color: white;
        }
        
        .btn-info:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(23,162,184,0.3);
        }
        
        .score-display {
            background: white;
            border-radius: 15px;
            padding: 20px;
            margin: 20px 0;
            text-align: center;
            box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }
        
        .score-text {
            font-size: 1.5em;
            color: #2c3e50;
            margin-bottom: 10px;
        }
        
        .score-percentage {
            font-size: 3em;
            font-weight: bold;
            color: #27ae60;
        }
        
        .name-input-section {
            background: white;
            border-radius: 15px;
            padding: 40px;
            margin: 30px 0;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            text-align: center;
        }
        
        .name-card h2 {
            color: #2c3e50;
            margin-bottom: 15px;
            font-size: 2em;
        }
        
        .name-card p {
            color: #7f8c8d;
            margin-bottom: 30px;
            font-size: 1.1em;
        }
        
        .name-input {
            width: 100%;
            max-width: 400px;
            padding: 15px;
            font-size: 1.2em;
            border: 2px solid #bdc3c7;
            border-radius: 10px;
            margin-bottom: 25px;
            text-align: center;
            transition: all 0.3s ease;
        }
        
        .name-input:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 10px rgba(52,152,219,0.3);
        }
        
        @media (max-width: 768px) {
            body { padding: 10px; }
            .main-title { font-size: 2em; }
            .info-grid { grid-template-columns: 1fr; }
            .speed-controls { justify-content: center; }
            .action-buttons { flex-direction: column; align-items: center; }
            .btn { width: 200px; }
        }
        
        .fade-in {
            animation: fadeIn 0.5s ease-in;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
    </style>
</head>
<body>
    <!-- Header Section -->
    <div class="header-section fade-in">
        <h1 class="main-title">🎧 듣기 문항 연습</h1>
        <div class="info-grid">
            <div class="info-item">
                <strong>학년:</strong> ${data.metadata.schoolLevel} ${data.metadata.grade}학년
            </div>
            <div class="info-item">
                <strong>주제:</strong> ${data.metadata.topic}
            </div>
            <div class="info-item">
                <strong>문항 수:</strong> ${data.questions.length}개
            </div>
            <div class="info-item">
                <strong>제한시간:</strong> 없음
            </div>
        </div>
    </div>
    
    <!-- Audio Section (hidden initially) -->
    <div class="audio-section fade-in" id="audioSection" style="display:none;">
        <h2 class="audio-title">🎵 듣기 파일</h2>
        <audio id="listeningAudio" controls>
            <source src="data:audio/mp3;base64,${data.audioBase64}" type="audio/mp3">
            죄송합니다. 브라우저에서 오디오를 지원하지 않습니다.
        </audio>
        
        <div class="speed-controls">
            <button class="speed-btn" onclick="changeSpeed(0.75)" data-speed="0.75">0.75x</button>
            <button class="speed-btn active" onclick="changeSpeed(1.0)" data-speed="1.0">1.0x</button>
            <button class="speed-btn" onclick="changeSpeed(1.25)" data-speed="1.25">1.25x</button>
            <button class="speed-btn" onclick="changeSpeed(1.5)" data-speed="1.5">1.5x</button>
        </div>
        
        <div class="play-counter">
            <span id="playCount">0</span> / ${data.playSettings.maxPlays} 재생
        </div>
    </div>
    
    <!-- Questions Section (hidden initially) -->
    <div class="questions-section" id="questionsSection" style="display:none;">
        ${questionsHTML}
    </div>
    
    <!-- Student Name Input Section (shown initially) -->
    <div class="name-input-section" id="nameInputSection">
        <div class="name-card">
            <h2>👋 학습자 정보 입력</h2>
            <p>듣기 연습을 시작하기 전에 이름을 입력해주세요.</p>
            <input type="text" id="studentName" placeholder="이름을 입력하세요 (Enter로 시작)" class="name-input">
            <button class="btn btn-primary" onclick="startListening()">듣기 연습 시작</button>
            <p class="text-xs text-gray-500 mt-2">💡 이름 입력 후 Enter를 누르거나 버튼을 클릭하세요</p>
        </div>
    </div>

    <!-- Action Buttons (hidden initially) -->
    <div class="action-buttons" id="actionButtons" style="display:none;">
        <button class="btn btn-primary" onclick="checkAnswers()">정답 확인</button>
        <button class="btn btn-info" onclick="resetQuiz()">다시 풀기</button>
    </div>
    
    <!-- Score Display -->
    <div class="score-display" id="scoreDisplay" style="display:none;">
        <div class="score-text">최종 점수</div>
        <div class="score-percentage" id="scorePercentage">0%</div>
    </div>
    
    <script>
        // LearningLogger 클래스 정의 (임베디드)
        class LearningLogger {
            constructor(options = {}) {
                this.sheetId = options.sheetId || this.extractSheetId(options.sheetUrl);
                this.webAppUrl = options.webAppUrl || 'https://script.google.com/macros/s/AKfycbzKJqOo1dqcTlT4TSB51e3EsHH5EFm7m6zcsLMWUQWSOGXx6MDryptjhiUj04oMmA3b/exec';
                this.templateId = options.templateId || 'unknown';
                this.isEnabled = options.isEnabled !== false;
                
                this.sessionData = {
                    studentName: '',
                    sessionStartTime: null,
                    totalLearningTime: 0,
                    correctAnswers: 0,
                    wrongAnswers: 0,
                    skippedAnswers: 0,
                    hintsUsed: false,
                    additionalData: {},
                    logTime: null
                };
            }

            extractSheetId(url) {
                if (!url) return null;
                const match = url.match(/\\/spreadsheets\\/d\\/([a-zA-Z0-9-_]+)/);
                return match ? match[1] : null;
            }

            startSession(studentName, additionalData = {}) {
                this.sessionData.studentName = studentName;
                this.sessionData.sessionStartTime = new Date();
                this.sessionData.additionalData = { ...additionalData };
                console.log('📚 학습 세션 시작:', studentName);
            }

            recordCorrectAnswer() { this.sessionData.correctAnswers++; }
            recordWrongAnswer() { this.sessionData.wrongAnswers++; }
            recordSkippedAnswer() { this.sessionData.skippedAnswers++; }
            recordHintUsed() { this.sessionData.hintsUsed = true; }
            setAdditionalData(key, value) { this.sessionData.additionalData[key] = value; }

            calculateLearningTime() {
                if (!this.sessionData.sessionStartTime) return 0;
                const endTime = new Date();
                return Math.round((endTime - this.sessionData.sessionStartTime) / 1000);
            }

            async endSession(additionalSessionData = {}) {
                if (!this.isEnabled || !this.sheetId) {
                    console.log('📝 로그 기록이 비활성화되어 있습니다.');
                    return { success: true, message: '로그 기록 비활성화' };
                }

                this.sessionData.totalLearningTime = this.calculateLearningTime();
                this.sessionData.logTime = new Date();
                this.sessionData.additionalData = { ...this.sessionData.additionalData, ...additionalSessionData };

                const logData = this.formatLogData();
                
                try {
                    const result = await this.sendToGoogleSheet(logData);
                    return result;
                } catch (error) {
                    console.error('❌ 로그 저장 실패:', error);
                    return { success: false, error: error.message };
                }
            }

            formatLogData() {
                const totalSeconds = this.sessionData.totalLearningTime;
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                const formattedTime = minutes + '분 ' + seconds + '초';

                return {
                    기능명: this.templateId,
                    학생이름: this.sessionData.studentName,
                    접속시간: this.sessionData.sessionStartTime.toLocaleString('ko-KR'),
                    학습시간: formattedTime,
                    정오답정보: '정답:' + this.sessionData.correctAnswers + ', 오답:' + this.sessionData.wrongAnswers + ', 스킵:' + this.sessionData.skippedAnswers,
                    힌트사용여부: this.sessionData.hintsUsed,
                    부가정보: JSON.stringify(this.sessionData.additionalData),
                    기록시간: this.sessionData.logTime.toLocaleString('ko-KR')
                };
            }

            async sendToGoogleSheet(logData) {
                if (!this.sheetId) {
                    throw new Error('구글 시트 ID가 설정되지 않았습니다.');
                }

                const form = new URLSearchParams();
                form.append('sheetId', this.sheetId);
                form.append('menu', 'learning_log');
                
                Object.entries(logData).forEach(([key, value]) => {
                    form.append(key, value);
                });

                console.log('📤 구글 시트로 로그 전송:', logData);

                await fetch(this.webAppUrl, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: form.toString()
                });

                console.log('✅ 로그 전송 완료');
                return { success: true, message: '로그가 구글 시트에 저장되었습니다.' };
            }

            resetSession() {
                this.sessionData = {
                    studentName: '',
                    sessionStartTime: null,
                    totalLearningTime: 0,
                    correctAnswers: 0,
                    wrongAnswers: 0,
                    skippedAnswers: 0,
                    hintsUsed: false,
                    additionalData: {},
                    logTime: null
                };
            }
        }

        // 학습 로그 시스템 설정
        const learningLogger = new LearningLogger({
            templateId: '듣기연습',
            sheetUrl: '${data.metadata.sheetUrl || ''}',
            isEnabled: ${!!data.metadata.sheetUrl}
        });

        let playCount = 0;
        const maxPlays = ${data.playSettings.maxPlays};
        let currentScore = 0;
        let totalQuestions = ${data.questions.length};
        let answered = [];
        let isStarted = false;
        
        // 오디오 재생 제한
        document.getElementById('listeningAudio').addEventListener('play', function() {
            if (playCount >= maxPlays) {
                alert('⚠️ 최대 재생 횟수(' + maxPlays + '회)에 도달했습니다.');
                this.pause();
                return false;
            }
            playCount++;
            document.getElementById('playCount').textContent = playCount;
        });
        
        // 학습 시작 함수
        function startListening() {
            const studentName = document.getElementById('studentName').value.trim();
            
            if (!studentName) {
                alert('이름을 입력해주세요.');
                document.getElementById('studentName').focus();
                return;
            }
            
            // 학습 세션 시작
            learningLogger.startSession(studentName, {
                schoolLevel: '${data.metadata.schoolLevel}',
                grade: '${data.metadata.grade}',
                topic: '${data.metadata.topic}',
                questionsCount: totalQuestions,
                audioId: '${data.listeningId}'
            });
            
            // UI 전환
            document.getElementById('nameInputSection').style.display = 'none';
            document.getElementById('audioSection').style.display = 'block';
            document.getElementById('questionsSection').style.display = 'block';
            document.getElementById('actionButtons').style.display = 'block';
            
            isStarted = true;
            console.log('📚 듣기 학습 시작:', studentName);
        }

        // 속도 조절 함수
        function changeSpeed(rate) {
            document.getElementById('listeningAudio').playbackRate = rate;
            
            // 활성 버튼 스타일 변경
            document.querySelectorAll('.speed-btn').forEach(btn => {
                btn.classList.remove('active');
            });
            document.querySelector('[data-speed="' + rate + '"]').classList.add('active');
        }
        
        // 옵션 클릭 이벤트
        document.addEventListener('DOMContentLoaded', function() {
            document.querySelectorAll('.option-item').forEach(option => {
                option.addEventListener('click', function() {
                    const questionIndex = this.dataset.question;
                    const optionIndex = this.dataset.option;
                    
                    // 같은 문제의 다른 선택지들 선택 해제
                    document.querySelectorAll('[data-question="' + questionIndex + '"]').forEach(opt => {
                        opt.querySelector('input').checked = false;
                    });
                    
                    // 현재 선택지 선택
                    this.querySelector('input').checked = true;
                    
                    // 문제 카드에 answered 클래스 추가
                    document.getElementById('question-' + questionIndex).classList.add('answered');
                    
                    answered[questionIndex] = parseInt(optionIndex);
                });
            });
        });
        
        // 정답 확인
        function checkAnswers() {
            let correct = 0;
            let incorrect = 0;
            let skipped = 0;
            const correctAnswers = [${data.questions.map(q => q.correctIndex).join(', ')}];
            
            for (let i = 0; i < totalQuestions; i++) {
                const questionCard = document.getElementById('question-' + i);
                const options = document.querySelectorAll('[data-question="' + i + '"]');
                
                if (answered[i] !== undefined) {
                    if (answered[i] === correctAnswers[i]) {
                        correct++;
                        learningLogger.recordCorrectAnswer();
                        questionCard.classList.add('correct');
                        questionCard.classList.remove('incorrect');
                        
                        // 정답 선택지 표시
                        options[answered[i]].classList.add('correct');
                    } else {
                        incorrect++;
                        learningLogger.recordWrongAnswer();
                        questionCard.classList.add('incorrect');
                        questionCard.classList.remove('correct');
                        
                        // 오답 선택지 표시
                        options[answered[i]].classList.add('incorrect');
                        // 정답 선택지도 표시
                        options[correctAnswers[i]].classList.add('correct');
                    }
                } else {
                    skipped++;
                    learningLogger.recordSkippedAnswer();
                }
            }
            
            currentScore = Math.round((correct / totalQuestions) * 100);
            
            // 추가 학습 데이터 저장
            learningLogger.setAdditionalData('finalScore', currentScore);
            learningLogger.setAdditionalData('audioPlayCount', playCount);
            learningLogger.setAdditionalData('maxPlays', maxPlays);
            
            // 점수 표시
            document.getElementById('scorePercentage').textContent = currentScore + '%';
            document.getElementById('scoreDisplay').style.display = 'block';
            
            // 학습 세션 종료 및 로그 저장
            learningLogger.endSession({
                totalQuestions: totalQuestions,
                correctAnswers: correct,
                incorrectAnswers: incorrect,
                skippedAnswers: skipped,
                finalScore: currentScore + '%',
                audioPlayCount: playCount
            }).then(result => {
                if (result.success) {
                    console.log('✅ 학습 로그 저장 완료:', result.message);
                } else {
                    console.log('⚠️ 학습 로그 저장 실패:', result.error);
                }
            });
            
            // 해설 자동으로 보여주기
            setTimeout(showAllExplanations, 1000);
        }
        
        // 해설 보기
        function showAllExplanations() {
            document.querySelectorAll('.explanation-box').forEach((el, index) => {
                setTimeout(() => {
                    el.style.display = 'block';
                    el.classList.add('fade-in');
                }, index * 200);
            });
        }
        
        // 퀴즈 리셋
        function resetQuiz() {
            if (confirm('🔄 정말로 다시 시작하시겠습니까?')) {
                // 모든 선택 해제
                document.querySelectorAll('input[type="radio"]').forEach(input => {
                    input.checked = false;
                });
                
                // 모든 클래스 제거
                document.querySelectorAll('.question-card').forEach(card => {
                    card.classList.remove('answered', 'correct', 'incorrect');
                });
                
                document.querySelectorAll('.option-item').forEach(option => {
                    option.classList.remove('correct', 'incorrect');
                });
                
                // 해설 숨기기
                document.querySelectorAll('.explanation-box').forEach(el => {
                    el.style.display = 'none';
                });
                
                // UI 초기화
                document.getElementById('scoreDisplay').style.display = 'none';
                document.getElementById('nameInputSection').style.display = 'block';
                document.getElementById('audioSection').style.display = 'none';
                document.getElementById('questionsSection').style.display = 'none';
                document.getElementById('actionButtons').style.display = 'none';
                
                // 변수 초기화
                answered = [];
                currentScore = 0;
                playCount = 0;
                isStarted = false;
                document.getElementById('playCount').textContent = playCount;
                document.getElementById('studentName').value = '';
                
                // 학습 로그 초기화
                learningLogger.resetSession();
                
                // 맨 위로 스크롤
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
        
        // 키보드 단축키
        document.addEventListener('keydown', function(e) {
            // 입력 필드에서 키 이벤트가 발생한 경우 무시
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                if (e.key === 'Enter' && e.target.id === 'studentName') {
                    // 이름 입력 필드에서 Enter를 누르면 학습 시작
                    e.preventDefault();
                    startListening();
                }
                return; // 다른 입력 필드에서는 기본 동작 유지
            }
            
            // 학습이 시작된 상태에서만 단축키 활성화
            if (isStarted) {
                if (e.key === ' ') { // 스페이스바로 재생/일시정지
                    e.preventDefault();
                    const audio = document.getElementById('listeningAudio');
                    if (audio.paused) {
                        audio.play();
                    } else {
                        audio.pause();
                    }
                } else if (e.key === 'Enter') { // 엔터로 정답 확인
                    e.preventDefault();
                    checkAnswers();
                }
            }
        });
        
        // Form submit 방지 (혹시 모를 form 요소가 있을 경우)
        document.addEventListener('submit', function(e) {
            e.preventDefault();
            console.log('Form submit 이벤트가 차단되었습니다.');
            return false;
        });

        // 이름 입력 필드에 직접 이벤트 연결
        document.addEventListener('DOMContentLoaded', function() {
            const nameInput = document.getElementById('studentName');
            if (nameInput) {
                nameInput.addEventListener('keydown', function(e) {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        startListening();
                        return false;
                    }
                });
                
                // 포커스 자동 설정
                nameInput.focus();
            }
        });

        // 페이지 로드 시 애니메이션
        window.addEventListener('load', function() {
            document.querySelectorAll('.question-card').forEach((card, index) => {
                setTimeout(() => {
                    card.classList.add('fade-in');
                }, index * 150);
            });
        });
    </script>
</body>
</html>`;
  };


  const handlePassageGeneration = async () => {
    const { schoolLevel, grade, topic, passageSheetUrl } = passageSettings;
    
    if (!schoolLevel || !grade || !topic) {
      alert('모든 항목을 입력해주세요.');
      return;
    }
    
    // AbortController 생성
    const abortController = new AbortController();
    setPassageAbortController(abortController);
    
    setIsGeneratingPassage(true);
    updateProgress(0, '선택한 학교급에 맞는 어휘 데이터를 불러오고 있습니다...');
    
    try {
      // 학교급별 어휘 데이터 로드
      const vocabularyData = await loadWordsByLevel(schoolLevel, parseInt(grade));
      console.log('로드된 어휘 데이터:', vocabularyData.length);
      
      if (!vocabularyData || vocabularyData.length === 0) {
        alert('선택한 학교급의 어휘 데이터를 찾을 수 없습니다.');
        setIsGeneratingPassage(false);
        setPassageGenerationProgress(null);
        return;
      }

      updateProgress(1, `${vocabularyData.length}개의 어휘를 활용하여 AI가 지문을 생성하고 있습니다...`);

      // GPT API 호출하여 지문 생성
      const response = await fetch('/api/generate-passage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic: topic,
          gradeLevel: `${schoolLevel} ${grade}`,
          vocabulary: vocabularyData,
          targetLength: 300,
          passageSheetUrl: passageSheetUrl
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error('지문 생성 API 호출 실패');
      }

      updateProgress(2, 'AI가 생성한 지문을 분석하고 문법 해설을 준비하고 있습니다...');

      const result = await response.json();
      
      if (result.success) {
        updateProgress(3, '학습 워크시트 HTML 파일을 생성하고 있습니다...');
        // 1. 텍스트 파일 다운로드
        const textBlob = new Blob([result.passage], { type: 'text/plain; charset=utf-8' });
        const textUrl = URL.createObjectURL(textBlob);
        const textLink = document.createElement('a');
        textLink.href = textUrl;
        textLink.download = `지문_${topic}_${schoolLevel}_${grade}학년.txt`;
        document.body.appendChild(textLink);
        textLink.click();
        document.body.removeChild(textLink);
        URL.revokeObjectURL(textUrl);

        // 2. HTML 워크시트 생성 및 다운로드
        try {
          const passageData = {
            topic: topic,
            gradeLevel: `${schoolLevel} ${grade}학년`,
            passage: result.passage
          };

          const analysisData = {
            wordCount: result.metadata.wordCount,
            vocabularyUsageRate: result.metadata.vocabularyUsageRate,
            usedVocabularyCount: result.metadata.usedVocabularyCount,
            sentenceCount: result.metadata.sentenceCount,
            keyVocabulary: result.metadata.keyVocabulary || [],
            grammarPoints: result.metadata.grammarPoints || [],
            explanation: result.metadata.explanation || '지문에 대한 상세 해설이 제공됩니다.'
          };

          const htmlContent = await generatePassageWorksheetHTML(passageData, vocabularyData, analysisData);
          
          updateProgress(4, '배포를 위해 준비하고 있습니다...');
          
          // DeploymentModal 열기
          const schoolLevelEng = schoolLevel === '초등' ? 'elementary' : schoolLevel === '중등' ? 'middle' : 'high';
          const fileName = `reading_${schoolLevelEng}_${grade}_${topic.replace(/\s+/g, '-')}.html`;
          
          setDeploymentData({
            htmlContent: htmlContent,
            fileName: fileName,
            contentType: 'reading'
          });
          
          // 모든 단계 완료
          updateProgress(5, '지문 워크시트가 성공적으로 생성되었습니다!');
          
          // 진행률 표시를 잠시 보여준 후 DeploymentModal 열기
          setTimeout(() => {
            setShowDeploymentModal(true);
            setIsGeneratingPassage(false);
            setPassageGenerationProgress(null);
          }, 1000);
          
          // 설정 화면 닫기
          setShowPassageSettings(false);
          setPassageSettings({ schoolLevel: '', grade: '', topic: '', passageSheetUrl: '' });
        } catch (htmlError) {
          console.error('HTML 워크시트 생성 오류:', htmlError);
          alert('지문은 생성되었지만, 워크시트 생성 중 오류가 발생했습니다.');
          setIsGeneratingPassage(false);
          setPassageGenerationProgress(null);
        }
      } else {
        alert(`지문 생성 실패: ${result.error}`);
        setIsGeneratingPassage(false);
        setPassageGenerationProgress(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('지문 생성이 취소되었습니다.');
        alert('지문 생성이 취소되었습니다.');
      } else {
        console.error('지문 생성 오류:', error);
        alert(`지문 생성 중 오류가 발생했습니다: ${error.message}`);
      }
      setIsGeneratingPassage(false);
      setPassageGenerationProgress(null);
      setPassageAbortController(null);
    }
  };

  const handleCancelPassageGeneration = () => {
    if (passageAbortController) {
      passageAbortController.abort();
      setPassageAbortController(null);
    }
    setIsGeneratingPassage(false);
    setPassageGenerationProgress(null);
    setShowPassageSettings(false);
  };


  const handleGenerateFlashCard = async () => {
    const { schoolLevel, grade, wordCount } = wordCardSettings;
    
    if (!schoolLevel || !grade || !wordCount) {
      alert('모든 항목을 선택해주세요.');
      return;
    }
    
    try {
      // 진행률 시작
      setIsGeneratingTemplate(true);
      setCurrentTemplateType('단어카드');
      updateTemplateProgress(0, '단어 데이터를 로딩하고 있습니다...');
      
      const words = await generateWordData(schoolLevel, parseInt(grade), parseInt(wordCount));
      console.log('생성된 단어 데이터:', words);
      
      if (!words || words.length === 0) {
        alert('선택한 조건에 맞는 단어가 없습니다.');
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
        return;
      }
      
      updateTemplateProgress(1, '단어카드 HTML을 생성하고 있습니다...');
      const htmlContent = await generateFlashCardHTML(words, wordCardSettings, wordCardSettings.sheetUrl);
      console.log('생성된 HTML 길이:', htmlContent.length);
      console.log('HTML 내용 미리보기:', htmlContent.substring(0, 200) + '...');
      
      if (!htmlContent || htmlContent.length < 100) {
        alert('HTML 생성에 실패했습니다.');
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
        return;
      }
      
      updateTemplateProgress(2, '배포를 위해 준비하고 있습니다...');
      
      // DeploymentModal 열기
      const schoolLevelEng = schoolLevel === '초등' ? 'elementary' : schoolLevel === '중등' ? 'middle' : 'high';
      const fileName = `flashcard_${schoolLevelEng}_${grade}_${wordCount}words.html`;
      
      setDeploymentData({
        htmlContent: htmlContent,
        fileName: fileName,
        contentType: 'flashcards'
      });
      
      // 완료 후 잠시 대기 후 모달 열기
      setTimeout(() => {
        setShowDeploymentModal(true);
        setIsGeneratingTemplate(false);
        setTemplateGenerationProgress(null);
      }, 1000);
      
      // 설정 화면 닫기
      setShowWordCardSettings(false);
      setWordCardSettings({ schoolLevel: '', grade: '', wordCount: '', sheetUrl: '' });
    } catch (error) {
      console.error('플래시카드 생성 오류:', error);
      alert(`플래시카드 생성 중 오류가 발생했습니다: ${error.message}`);
      setIsGeneratingTemplate(false);
      setTemplateGenerationProgress(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header />

      {/* Navigation Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="bg-white rounded-lg shadow-md p-2 mb-8">
          <nav className="flex flex-wrap justify-center sm:justify-start">
            {Object.keys(tabNames).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 mx-1 my-1 rounded-lg font-medium text-sm sm:text-base transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-blue-500 text-white shadow-lg transform scale-105'
                    : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                }`}
              >
                {tabNames[tab]}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
          {menuItems[activeTab].map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full mx-auto mb-4 group-hover:from-blue-500 group-hover:to-blue-700 transition-all duration-300">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {activeTab === 'listening' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M6.343 6.343a8 8 0 000 11.314l1.414-1.414a6 6 0 000-8.486L6.343 6.343z"
                      />
                    )}
                    {activeTab === 'writing' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    )}
                    {activeTab === 'reading' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    )}
                    {activeTab === 'vocabulary' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                      />
                    )}
                    {activeTab === 'aiEducation' && (
                       <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Zm.75-12h9v9h-9v-9Z" />
                    )}
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 text-center mb-3 group-hover:text-blue-600 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm text-center leading-relaxed">
                  {item.description}
                </p>
              </div>
              <div className="px-6 pb-6">
                <button 
                  onClick={() => {
                    if (activeTab === 'listening' && item.title === '수준별 듣기 문항 생성') {
                      handleListeningClick();
                    } else if (activeTab === 'listening' && item.title === '쉐도잉 기능') {
                      handleShadowingClick();
                    } else if (activeTab === 'vocabulary' && item.title === '단어 카드 만들기') {
                      handleWordCardClick();
                    } else if (activeTab === 'vocabulary' && item.title === '객관식 퀴즈') {
                      handleQuizClick();
                    } else if (activeTab === 'vocabulary' && item.title === '주관식 퀴즈') {
                      handleShortAnswerClick();
                    } else if (activeTab === 'reading' && item.title === '지문 생성') {
                      handlePassageGenerationClick();
                    } else if (activeTab === 'reading' && item.title === '문항 생성') {
                      handleQuestionGenerationClick();
                    } else if (activeTab === 'writing' && item.title === '에세이 자동채점') {
                      handleEssayGradingClick();
                    } else if (activeTab === 'writing' && item.title === 'AI 맞춤 진로경로') {
                      handleCareerPathClick();
                    } else if (activeTab === 'aiEducation' && item.title === 'AI 기반 피드백 추천') {
                      // 새 탭에서 AI 대시보드 열기
                      window.open('?mode=dashboard', '_blank');
                    } else if (activeTab === 'aiEducation' && item.title === 'AI 기반 학습자료 확장') {
                      // 새 탭에서 인터랙티브 편집기 열기
                      window.open('?mode=interactive-editor', '_blank');
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform group-hover:scale-105"
                >
                  시작하기
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* 단어 카드 설정 모달 */}
        <WordSettingsModal
          isOpen={showWordCardSettings}
          onClose={() => {
            setShowWordCardSettings(false);
            setWordCardSettings({ schoolLevel: '', grade: '', wordCount: '', sheetUrl: '' });
          }}
          settings={wordCardSettings}
          onSettingsChange={setWordCardSettings}
          onConfirm={handleGenerateFlashCard}
          title="단어 카드 설정"
          isGenerating={isGeneratingTemplate && currentTemplateType === '단어카드'}
          generationProgress={templateGenerationProgress}
        />

        {/* 객관식 퀴즈 설정 모달 */}
        <WordSettingsModal
          isOpen={showQuizSettings}
          onClose={() => {
            setShowQuizSettings(false);
            setQuizSettings({ schoolLevel: '', grade: '', wordCount: '', sheetUrl: '' });
          }}
          settings={quizSettings}
          onSettingsChange={setQuizSettings}
          onConfirm={handleQuizSettingsConfirm}
          title="객관식 퀴즈 설정"
          isGenerating={isGeneratingTemplate && currentTemplateType === '객관식 퀴즈'}
          generationProgress={templateGenerationProgress}
        />

        {/* 주관식 퀴즈 설정 모달 */}
        <WordSettingsModal
          isOpen={showShortAnswerSettings}
          onClose={() => {
            setShowShortAnswerSettings(false);
            setShortAnswerSettings({ schoolLevel: '', grade: '', wordCount: '', sheetUrl: '' });
          }}
          settings={shortAnswerSettings}
          onSettingsChange={setShortAnswerSettings}
          onConfirm={handleShortAnswerSettingsConfirm}
          title="주관식 퀴즈 설정"
          isGenerating={isGeneratingTemplate && currentTemplateType === '주관식 퀴즈'}
          generationProgress={templateGenerationProgress}
        />

        {/* 듣기 문항 생성 설정 모달 */}
        <ListeningGenerationModal
          isOpen={showListeningSettings}
          onClose={() => {
            setShowListeningSettings(false);
            setListeningSettings({ schoolLevel: '', grade: '', topic: '', sheetUrl: '' });
          }}
          settings={listeningSettings}
          onSettingsChange={setListeningSettings}
          onConfirm={handleListeningGeneration}
          title="수준별 듣기 문항 생성"
          isGenerating={isGeneratingListening}
        />


        {/* 지문 생성 설정 모달 */}
        <PassageSettingsModal
          isOpen={showPassageSettings}
          onClose={handleCancelPassageGeneration}
          settings={passageSettings}
          onSettingsChange={setPassageSettings}
          onConfirm={handlePassageGeneration}
          title="지문 생성 설정"
          isGenerating={isGeneratingPassage}
          generationProgress={passageGenerationProgress}
        />

        {/* 배포 모달 */}
        <DeploymentModal
          isOpen={showDeploymentModal}
          htmlContent={deploymentData.htmlContent}
          fileName={deploymentData.fileName}
          contentType={deploymentData.contentType}
          onClose={() => setShowDeploymentModal(false)}
        />

      </div>

    </div>
  );
};

export default EnglishStudyComponent;